import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import Swal from "sweetalert2";
import enteFunciones from "../function/ente.Funcion";

const RegistrarEnteVsJefe = () => {
  const { id, idasociado } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);

  const [jefes, setJefes] = useState([]);
  const [jefeSeleccionado, setJefeSeleccionado] = useState(null);
  const [estadoAsignacion, setEstadoAsignacion] = useState("");

  // ✅ Modo edición cuando viene idasociado (asociación existente)
  const isEdit = useMemo(() => Boolean(idasociado), [idasociado]);

  const cleanData = useCallback(() => {
    setJefeSeleccionado(null);
    setEstadoAsignacion("");
  }, []);

  // 🎨 Estilos react-select (para que el menú no quede “metido” y se vea completo)
  const selectStyles = useMemo(
    () => ({
      container: (base) => ({ ...base, width: "100%" }),
      control: (base, state) => ({
        ...base,
        minHeight: 38,
        borderColor: state.isFocused ? "#86b7fe" : base.borderColor,
        boxShadow: state.isFocused ? "0 0 0 .25rem rgba(13,110,253,.25)" : base.boxShadow,
        "&:hover": { borderColor: state.isFocused ? "#86b7fe" : base.borderColor },
      }),
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      menu: (base) => ({ ...base, zIndex: 9999 }),
    }),
    []
  );

  // 📥 Cargar jefes
  const cargarJefes = useCallback(async () => {
    try {
      setLoading(true);
      const jefesData = await enteFunciones.obtenerJefes();

      const jefesProcesados = (jefesData || []).map((jefe) => ({
        ...jefe,
        nombreJefe: [
          jefe.nombre1Jefe,
          jefe.nombre2Jefe,
          jefe.apellido1Jefe,
          jefe.apellido2Jefe,
        ]
          .filter(Boolean)
          .join(" "),
      }));

      setJefes(jefesProcesados);
    } catch (error) {
      console.error("Error al cargar los jefes:", error?.message);
      Swal.fire("Error", "No se pudo cargar la lista de jefes.", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargarJefes();
  }, [cargarJefes]);

  // 📥 Cargar asociación si es edición
  const fetchAsociacion = useCallback(async () => {
    if (!idasociado) return;
    try {
      setLoading(true);

      const response = await axios.get(`/api/asociarjefes/listid/${idasociado}`);
      const data = response.data?.asociar_asignar;

      setEstadoAsignacion(data?.estadoAsignacion || "");

      const jefe = data?.jefeObjectId; // populate
      if (jefe) {
        const nombreCompletoJefe = [
          jefe.nombre1Jefe,
          jefe.nombre2Jefe,
          jefe.apellido1Jefe,
          jefe.apellido2Jefe,
        ]
          .filter(Boolean)
          .join(" ");

        setJefeSeleccionado({
          value: jefe.documentoJefe,
          label: `CC: ${jefe.documentoJefe} - ${nombreCompletoJefe}`,
        });
      }
    } catch (error) {
      console.error("Error al cargar la asociación:", error?.message);
      Swal.fire("Error", "No se pudo cargar la asociación.", "error");
    } finally {
      setLoading(false);
    }
  }, [idasociado]);

  useEffect(() => {
    if (idasociado) fetchAsociacion();
  }, [idasociado, fetchAsociacion]);

  const opcionesJefes = useMemo(
    () =>
      (jefes || []).map((jefe) => ({
        value: jefe.documentoJefe,
        label: `CC: ${jefe.documentoJefe} - ${jefe.nombreJefe}`,
      })),
    [jefes]
  );

  const opcionesEstado = useMemo(
    () => [
      { value: "Activo", label: "Activo" },
      { value: "Inactivo", label: "Inactivo" },
    ],
    []
  );

  const saveAsignacion = async () => {
    if (!jefeSeleccionado || !estadoAsignacion) {
      Swal.fire({
        icon: "warning",
        title: "Campos incompletos",
        text: "Debe seleccionar un Jefe y un Estado.",
      });
      return;
    }

    try {
      setLoading(true);

      const newAsignacion = {
        documentoJefe: jefeSeleccionado.value,
        estadoAsignacion,
      };

      await axios.post(`/api/asociarjefes/add/${id}`, newAsignacion);

      await Swal.fire({
        icon: "success",
        title: "La asociación ha sido registrada con éxito",
        showConfirmButton: false,
        timer: 1500,
      });

      cleanData(); // ✅ al guardar
      navigate("/enteconformador/listar");
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Error al guardar la asignación",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateAsignacion = async () => {
    if (!jefeSeleccionado || !estadoAsignacion) {
      Swal.fire("Campos incompletos", "Debe seleccionar un Jefe y un Estado.", "warning");
      return;
    }

    try {
      setLoading(true);

      const actualizacion = {
        documentoJefe: jefeSeleccionado.value,
        estadoAsignacion,
      };

      await axios.put(`/api/asociarjefes/update/${idasociado}`, actualizacion);

      await Swal.fire({
        icon: "success",
        title: "La asociación fue actualizada con éxito",
        showConfirmButton: false,
        timer: 1500,
      });

      cleanData(); // ✅ al actualizar
      navigate("/enteconformador/ente/");
    } catch (error) {
      Swal.fire(
        "Error",
        error.response?.data?.message || "Error al actualizar la asignación",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const actions = async (e) => {
    e.preventDefault();
    isEdit ? await updateAsignacion() : await saveAsignacion();
  };

  const handleCancelar = () => {
    cleanData();
    navigate("/enteconformador/ente/");
  };

  return (
    <div className="bg-body-tertiary py-4">
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          {/* Header */}
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">
                  {isEdit ? "Editar Asociación" : "Asociar Jefe a Ente Conformador"}
                </h5>
                <small className="text-secondary">
                  Selecciona el jefe inmediato y define el estado de asignación.
                </small>
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <button
                  type="button"
                  className="btn btn-outline-dark"
                  onClick={() => navigate("/enteconformador/ente/")}
                  disabled={loading}
                  title="Volver">
                  <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                  Volver
                </button>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="p-3 p-md-4">
            {loading && (
              <div className="text-center text-secondary py-3">
                <div className="spinner-border" role="status" />
                <div className="mt-2">Procesando…</div>
              </div>
            )}

            <form id="form-asociacion" onSubmit={actions} noValidate>
              <div className="row g-3">
                {/* Jefe */}
                <div className="col-12 col-md-7">
                  <label className="form-label fw-semibold">Jefe Inmediato</label>

                  {isEdit ? (
                    <input
                      type="text"
                      className="form-control"
                      value={jefeSeleccionado?.label || ""}
                      readOnly
                      disabled
                    />
                  ) : (
                    <Select
                      options={opcionesJefes}
                      value={jefeSeleccionado}
                      onChange={setJefeSeleccionado}
                      placeholder="Seleccione un Jefe"
                      isClearable
                      styles={selectStyles}
                      menuPortalTarget={document.body} // ✅ evita que se corte dentro del contenedor
                    />
                  )}
                </div>

                {/* Estado */}
                <div className="col-12 col-md-5">
                  <label className="form-label fw-semibold">Estado de Asignación</label>
                  <Select
                    options={opcionesEstado}
                    value={opcionesEstado.find((op) => op.value === estadoAsignacion) || null}
                    onChange={(opt) => setEstadoAsignacion(opt?.value || "")}
                    placeholder="Seleccione un Estado"
                    isClearable
                    styles={selectStyles}
                    menuPortalTarget={document.body} // ✅ evita recorte
                  />
                </div>

                {/* Footer botones */}
                <div className="col-12 pt-2">
                  <div className="d-flex flex-wrap gap-2 justify-content-end">
                    <button
                      type="button"
                      className="btn btn-outline-dark"
                      onClick={cleanData}
                      disabled={loading}
                      title="Limpiar"
                    >
                      <i className="fa-solid fa-eraser me-2"></i>
                      Limpiar
                    </button>

                    <button
                      type="button"
                      className="btn btn-outline-danger"
                      onClick={handleCancelar}
                      disabled={loading}
                      title="Cancelar"
                    >
                      <i className="fa-solid fa-xmark me-2"></i>
                      Cancelar
                    </button>

                    <button
                      type="submit"
                      form="form-asociacion"
                      className="btn btn-dark"
                      disabled={loading}
                      title={isEdit ? "Actualizar" : "Guardar"}
                    >
                      <i
                        className={`fa-solid ${
                          loading ? "fa-spinner fa-spin" : "fa-floppy-disk"
                        } me-2`}
                      ></i>
                      {isEdit ? "Actualizar" : "Guardar"}
                    </button>
                  </div>
                </div>
              </div>
            </form>

          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrarEnteVsJefe;
